/**
 * Data quality (wave 20; docs/architecture/data-quality.md): the tenant's tracking
 * baseline — `GET`/`PUT /api/v1/data-quality/baseline` — and the `data_quality` block
 * the report routes embed beside their figures.
 *
 * The baseline (`tracking_reliable_from`) is the first tenant day from which browser
 * tracking is reliable. A report says when its range or its comparison starts before
 * it, and how many of the range's orders carry a tracked visit (`coverage`). The block
 * is optional on every report: an API older than the coverage card sends none, and a
 * page then renders exactly as it did before.
 */

/** A row's credit (attribution.md §4): a credited touch, or no tracked touch at all ("Not tracked"). */
export type ConversionCredit = 'touch' | 'unattributed'

/** How many of the range's orders carry a tracked visit under the report's model. */
export interface TrackingCoverage {
  orders: number
  trackedOrders: number
  notTrackedOrders: number
  /** `tracked / orders` as the API's decimal string ("0.62": two places, half-up); null when there were no orders. */
  trackedShare: string | null
}

export interface DataQuality {
  /** The tenant day tracking is reliable from (`YYYY-MM-DD`); null when no baseline is set. */
  trackingReliableFrom: string | null
  /** The report's range starts before the baseline. */
  rangeBeforeBaseline: boolean
  /** The comparison range starts before the baseline; false when the report does not compare. */
  comparisonBeforeBaseline: boolean
  /** Null only where the API sent no coverage (the block before the coverage card). */
  coverage: TrackingCoverage | null
}

/** The tenant's baseline as `GET /data-quality/baseline` answers it: every field null when none was ever set. */
export interface TrackingBaseline {
  trackingReliableFrom: string | null
  note: string | null
  /** The subject of whoever last set or cleared it, as the audit log names actors. */
  setBy: string | null
  setAt: string | null
}

/** `PUT`'s body: the whole baseline, replaced. A null date clears it; a blank note is no note. */
export interface TrackingBaselineInput {
  trackingReliableFrom: string | null
  note: string | null
}

/** The most a note may hold (the API's `NOTE_LENGTH`). */
export const BASELINE_NOTE_MAX_LENGTH = 280

export interface DataQualityApi {
  /** `analytics.read`. */
  trackingBaseline(): Promise<TrackingBaseline>
  /** `settings.manage`. A date after today in the tenant's reporting time zone is a 422. */
  setTrackingBaseline(input: TrackingBaselineInput): Promise<TrackingBaseline>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface TrackingCoverageOut {
  orders: number
  tracked_orders: number
  not_tracked_orders: number
  tracked_share: string | null
}

export interface DataQualityOut {
  tracking_reliable_from: string | null
  range_before_baseline: boolean
  comparison_before_baseline: boolean
  coverage?: TrackingCoverageOut | null
}

export interface TrackingBaselineOut {
  tracking_reliable_from: string | null
  note: string | null
  set_by: string | null
  set_at: string | null
}

export function dataQualityFromWire(body: DataQualityOut | null | undefined): DataQuality | null {
  if (!body) return null
  const coverage = body.coverage
  return {
    trackingReliableFrom: body.tracking_reliable_from,
    rangeBeforeBaseline: body.range_before_baseline,
    comparisonBeforeBaseline: body.comparison_before_baseline,
    coverage: coverage
      ? {
          orders: coverage.orders,
          trackedOrders: coverage.tracked_orders,
          notTrackedOrders: coverage.not_tracked_orders,
          trackedShare: coverage.tracked_share,
        }
      : null,
  }
}

export function trackingBaselineFromWire(body: TrackingBaselineOut): TrackingBaseline {
  return {
    trackingReliableFrom: body.tracking_reliable_from,
    note: body.note,
    setBy: body.set_by,
    setAt: body.set_at,
  }
}

/** The `PUT` body. A blank note is sent as none, which is what the API makes of it anyway. */
export function trackingBaselineToWire(input: TrackingBaselineInput): { tracking_reliable_from: string | null; note: string | null } {
  const note = input.note?.trim() ?? ''
  return { tracking_reliable_from: input.trackingReliableFrom, note: note === '' ? null : note }
}

export const DATA_QUALITY_BASELINE_PATH = '/data-quality/baseline'
